// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { updateDryEyeProfile } from "../../../api";
import { DryEyeProfileType } from "../../../types";

export const useUpdateDryEyeProfile = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (profileData: DryEyeProfileType) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateDryEyeProfile(token, clinicId, profileData);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate clinic lens query cache
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dry-eye-profiles", clinicId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Option updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Option could not be updated!"),
      });
    },
  });
};
